import { useEffect, useState } from 'react'
import type { LocalResponse, LocalSession } from '../../db/types'
import { completeSession, loadSessionResult } from '../../offline/session'
import { useOnline, useSyncCounts } from '../../shared/hooks'

export interface SessionResultState {
  /** `null` while loading, and for a session id IndexedDB does not hold. */
  session: LocalSession | null
  responses: LocalResponse[]
  online: boolean
  /** Responses not yet acknowledged by the server. */
  waiting: number
  /** A settle attempt is in flight. */
  settling: boolean
}

/**
 * The stored session and its responses, plus §5.1's settle-on-reconnect: a
 * session finished offline is completed against the server once the device is
 * online and the response queue has drained.
 *
 * One settle attempt per change of `online`, `status` or "queue drained". The
 * effect reacts to those values, never to the session object: every read from
 * IndexedDB is a new object, so depending on it would retry `POST /complete`
 * in a tight loop for as long as the server keeps failing.
 */
export function useSessionResult(sessionId: string): SessionResultState {
  const online = useOnline()
  const counts = useSyncCounts()
  const waiting = counts.pending + counts.inflight + counts.failed

  const [session, setSession] = useState<LocalSession | null>(null)
  const [responses, setResponses] = useState<LocalResponse[]>([])
  const [settling, setSettling] = useState(false)
  // Bumped after a settle attempt so the load effect re-reads the result.
  const [revision, setRevision] = useState(0)

  useEffect(() => {
    // Loads from IndexedDB. Moves to a query hook with the Phase 4 data-layer work.
    let cancelled = false
    void loadSessionResult(sessionId).then((result) => {
      if (cancelled || !result) return
      setSession(result.session)
      setResponses(result.responses)
    })
    return () => {
      cancelled = true
    }
  }, [sessionId, revision])

  const status = session?.status
  const drained = waiting === 0
  useEffect(() => {
    if (!online || status !== 'completed') return
    let cancelled = false
    void (async () => {
      setSettling(true)
      try {
        await completeSession(sessionId)
      } finally {
        if (!cancelled) {
          setSettling(false)
          setRevision((r) => r + 1)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [online, status, drained, sessionId])

  return { session, responses, online, waiting, settling }
}
